import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  home: "Home", people: "People", recruiting: "Recruiting", jobs: "Jobs", tasks: "Tasks",
  calendar: "Calendar", communications: "Communications", training: "Training",
  documents: "Documents", reports: "Reports", admin: "Admin Center",
};

export default function RolesPermissions() {
  const { currentAccount } = useAuth();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/roles").then((r) => { setData(r.data); setSelected(r.data.roles[0]?.role); })
      .catch((e) => toast.error(apiError(e)));
  }, [currentAccount]);

  if (!data) return <PageLoader />;
  const role = data.roles.find((r) => r.role === selected);

  const toggle = async (module, value) => {
    const updated = { ...role.module_access, [module]: value };
    setData({ ...data, roles: data.roles.map((r) => r.role === selected ? { ...r, module_access: updated } : r) });
    try { await api.put(`/roles/${selected}`, { module_access: { [module]: value } }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Roles & Permissions" description="Control which modules each role can access. Finer-grained controls arrive in later phases." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-1.5">
          {data.roles.map((r) => (
            <button key={r.role} onClick={() => setSelected(r.role)} data-testid={`role-${r.role}`}
              className={`w-full text-left rounded-md border px-4 py-3 transition-colors ${selected === r.role ? "border-[#006090] bg-[#006090]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800 text-sm">{r.label}</span>
                {r.mfa_required && <span className="flex items-center gap-1 text-xs text-amber-600"><Lock className="w-3 h-3" /> MFA</span>}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{r.member_count} member{r.member_count !== 1 ? "s" : ""}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5 text-[#006090]" /><h3 className="font-display font-semibold text-slate-900">{role?.label}</h3></div>
          <p className="text-sm text-slate-500 mb-5">{role?.description}</p>
          {role?.mfa_required && <div className="mb-4"><StatusBadge status="active" label="MFA required for this role" /></div>}
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Module Access</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.modules.map((m) => (
              <div key={m} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/50 px-3 py-2.5">
                <span className="text-sm text-slate-700">{MODULE_LABELS[m] || m}</span>
                <Switch checked={!!role?.module_access?.[m]} onCheckedChange={(v) => toggle(m, v)} data-testid={`perm-${selected}-${m}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
